/**
 * Today's beat, and the per-outlet visit screen.
 *
 * The beat and its sequence are assigned by Admin — the salesperson does not
 * build his own, so this list is read-only ordering with three actions per
 * stop: book an order, record a collection, or log why neither happened.
 *
 * Cached for offline: the whole point is that it works in a market with no
 * signal, and every action queues.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { ClassificationBadge, classificationNameStyle } from '../../components/ClassificationBadge';
import {
  Banner, Button, Card, Chip, ChipRow, Empty, ErrorState, Money, Select,
  Sheet, Skeleton, TextArea, TextInput, useToast,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, useOnline } from '../../lib/useApi';
import { useCart } from '../../lib/cart';
import { useT } from '../../lib/i18n';
import { api, ApiError } from '../../lib/api';
import * as outbox from '../../lib/outbox';
import { money, dateShort } from '../../lib/format';
import { IconPhone, IconPin, IconChevron } from '../../components/Icons';

interface Outlet {
  stop_id?: string;
  sequence_no: number;
  priority: string;
  status?: 'pending' | 'visited' | 'skipped';
  skip_reason?: string | null;
  shop_id: string;
  shop_name: string;
  address_line1: string;
  landmark: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  open_time: string | null;
  close_time: string | null;
  customer_id: string;
  customer_name: string;
  customer_classification?: string | null;
  customer_code: string;
  last_order_date: string | null;
  outstanding_paise: number;
  /**
   * Who already called here today, if anybody. Null on a beat with one
   * salesperson, which is most of them.
   */
  visited_by?: string | null;
  outstandingDisplay: string;
  daysSinceLastOrder: number | null;
  mapsUrl: string | null;
  dialUrl: string | null;
}

interface BeatPlan {
  assigned: boolean;
  date: string;
  message?: string;
  generatedFromMaster?: boolean;
  beatPlanId?: string;
  beat?: { id: string; name: string; code: string };
  status?: string;
  outlets: Outlet[];
  summary?: { total: number; visited: number; skipped: number; pending: number };
}

type Filter = 'pending' | 'visited' | 'all';

export default function Beat() {
  const t = useT();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('pending');

  const { data, loading, error, stale, refetch } = useQuery<BeatPlan>(
    '/api/field/beat/today',
    { cacheKey: 'beat:today', ttlSeconds: 3600, refreshOn: ['orders', 'deliveries'] },
  );
  useRefreshOnFocus(refetch);

  const outlets = useMemo(() => {
    const list = data?.outlets ?? [];
    if (filter === 'all') return list;
    if (filter === 'visited') return list.filter((o) => o.status === 'visited' || o.status === 'skipped');
    return list.filter((o) => !o.status || o.status === 'pending');
  }, [data, filter]);

  if (loading && !data) return <Page title={t('beat.title')}><Skeleton rows={5} height={100} /></Page>;
  if (error && !data) {
    return <Page title={t('beat.title')}><ErrorState message={error.message} onRetry={refetch} /></Page>;
  }
  if (!data?.assigned) {
    return (
      <Page title={t('beat.title')}>
        <Empty title={t('beat.noBeat')} hint={data?.message} />
      </Page>
    );
  }

  const s = data.summary;

  return (
    <Page title={data.beat?.name ?? t('beat.title')} subtitle={dateShort(data.date)}>
      <div className="stack">
        {stale && <Banner tone="warn">{t('app.staleData')}</Banner>}

        {s && (
          <div className="row" style={{ gap: 8 }}>
            <Chip>{t('beat.outlets', { n: s.total })}</Chip>
            <Chip tone="ok">{s.visited} {t('beat.visited').toLowerCase()}</Chip>
            {s.skipped > 0 && <Chip tone="warn">{s.skipped} {t('beat.skipped').toLowerCase()}</Chip>}
            <Chip tone="info">{s.pending} {t('beat.pending').toLowerCase()}</Chip>
          </div>
        )}

        <ChipRow
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'pending', label: t('beat.pending'), count: s?.pending },
            { value: 'visited', label: t('beat.visited'), count: (s?.visited ?? 0) + (s?.skipped ?? 0) },
            { value: 'all', label: t('app.total'), count: s?.total },
          ]}
        />

        {outlets.length === 0 && <Empty title={filter === 'pending' ? 'All done for today' : undefined} />}

        <div className="stack-2">
          {outlets.map((o) => (
            <Card key={o.shop_id}>
              <div className="row-between" style={{ alignItems: 'flex-start' }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span
                      className="avatar"
                      style={{ width: 26, height: 26, flex: '0 0 26px', fontSize: 12 }}
                    >{o.sequence_no}</span>
                    <span className="t-semi t-clip" style={classificationNameStyle(o.customer_classification)}>{o.shop_name}</span>
                    {o.customer_classification && o.customer_classification !== 'trusted' && (
                      <ClassificationBadge classification={o.customer_classification} />
                    )}
                    {o.status === 'visited' && <Chip tone="ok">{t('beat.visited')}</Chip>}
                    {o.status === 'skipped' && <Chip tone="warn">{t('beat.skipped')}</Chip>}
                    {o.priority === 'high' && <Chip tone="danger">High</Chip>}
                  </div>

                  <div className="t-xs t-muted t-clip" style={{ marginTop: 4 }}>
                    {o.customer_code} · {o.address_line1}
                    {o.landmark && ` · ${o.landmark}`}
                  </div>

                  <div className="row-wrap" style={{ gap: 6, marginTop: 6 }}>
                    {/*
                      ALREADY VISITED, AND BY WHOM — first in the row.

                      Two salespeople can share a beat since 6 Oct and both
                      see every stop. This chip is what stops the second one
                      walking the first one's round: a shopkeeper asked for
                      an order twice in one morning is how a distributor
                      looks disorganised to the only person who matters.
                    */}
                    {o.visited_by && (
                      <Chip tone="ok">Visited by {o.visited_by}</Chip>
                    )}
                    {/* Outstanding on the card, so a salesperson knows the dues
                        before he takes another order. */}
                    {o.outstanding_paise > 0 && (
                      <Chip tone="warn">{t('beat.outstanding', { amount: money(o.outstanding_paise, { compact: true }) })}</Chip>
                    )}
                    {o.last_order_date ? (
                      <Chip outline>
                        {t('beat.lastOrder', { date: dateShort(o.last_order_date) })}
                        {o.daysSinceLastOrder !== null && o.daysSinceLastOrder > 30 && ' ⚠'}
                      </Chip>
                    ) : (
                      <Chip tone="info">{t('beat.neverOrdered')}</Chip>
                    )}
                    {o.open_time && o.close_time && (
                      <Chip outline>{o.open_time}–{o.close_time}</Chip>
                    )}
                  </div>
                </div>
              </div>

              <div className="row" style={{ gap: 8, marginTop: 12 }}>
                {o.dialUrl && (
                  <a className="btn btn--ghost btn--sm" href={o.dialUrl}>
                    <IconPhone width={15} height={15} />
                  </a>
                )}
                {o.mapsUrl && (
                  <a className="btn btn--ghost btn--sm" href={o.mapsUrl} target="_blank" rel="noreferrer">
                    <IconPin width={15} height={15} />
                  </a>
                )}
                <Button
                  size="sm" className="grow"
                  variant={o.status === 'visited' ? 'ghost' : 'primary'}
                  onClick={() => navigate(`/visit/${o.shop_id}`, {
                    state: {
                      shopName: o.shop_name,
                      customerId: o.customer_id,
                      customerName: o.customer_name,
                      stopId: o.stop_id,
                      outstandingPaise: o.outstanding_paise,
                    },
                  })}
                >
                  {o.status === 'visited' ? t('app.done') : t('beat.visit')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Visit screen
// ---------------------------------------------------------------------------

const OUTCOMES = [
  'order_booked', 'no_order', 'shop_closed', 'payment_promised',
  'follow_up_needed', 'collection_made', 'competitor_push', 'other',
] as const;

const NO_ORDER_REASONS = [
  'sufficient_stock', 'credit_issue', 'price_objection',
  'owner_absent', 'competitor_stock', 'seasonal_low', 'other',
] as const;

type Outcome = typeof OUTCOMES[number];

export function Visit() {
  const t = useT();
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();
  const cart = useCart();

  const state = (window.history.state?.usr ?? {}) as {
    shopName?: string; customerId?: string; customerName?: string;
    stopId?: string; outstandingPaise?: number;
  };

  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [noOrderReason, setNoOrderReason] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [busy, setBusy] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');

  async function logVisit() {
    if (!outcome || !shopId) return;
    // The outcome code is what turns "target missed" into a diagnosable
    // cause, so a no-order visit must say why.
    if (outcome === 'no_order' && !noOrderReason) {
      toast.show(t('visit.noOrderReason'), 'danger');
      return;
    }

    setBusy(true);
    const body = {
      customerShopId: shopId,
      outcome,
      noOrderReason: outcome === 'no_order' ? noOrderReason : undefined,
      discussionNotes: notes.trim() || undefined,
      followUpDate: followUp || undefined,
      competitorObserved: competitor.trim() || undefined,
      beatPlanStopId: state.stopId,
    };

    try {
      if (!online) {
        await outbox.enqueue({
          method: 'POST', path: '/api/field/visits', body,
          label: `${t('visit.title')} — ${state.shopName ?? ''}`, kind: 'visit',
        });
        toast.show(t('app.offlineHint'), 'ok');
      } else {
        await api.post('/api/field/visits', body);
        toast.show(t('app.done'), 'ok');
      }
      navigate('/beat');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function skip() {
    if (!state.stopId || !skipReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/field/beat/stops/${state.stopId}/skip`, { reason: skipReason.trim() });
      toast.show(t('beat.skipped'), 'ok');
      navigate('/beat');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
      setSkipOpen(false);
    }
  }

  return (
    <Page title={state.shopName ?? t('visit.title')} subtitle={state.customerName} back="/beat">
      <div className="stack">
        {state.outstandingPaise !== undefined && state.outstandingPaise > 0 && (
          <Banner tone="warn">
            <span className="t-sm">
              {t('ledger.outstanding')}: {money(state.outstandingPaise)}
            </span>
          </Banner>
        )}

        {/* The three primary actions, kept deliberately few. The requirement was
            Book Order, Record Collection, Mark No Order — anything more and the
            screen stops being a two-second interaction. */}
        <div className="stack-2">
          <Button
            block size="lg"
            disabled={!state.customerId}
            onClick={() => {
              if (!state.customerId) return;
              // Set the on-behalf customer on the CART, not in router state.
              //
              // This button used to pass `{ customerId, shopId }` as router
              // state, and nothing read it: `Catalogue` and `Cart` both took
              // the customer from `user.customerId`, which is null for a
              // salesperson. The result was an unpriced catalogue, no shops,
              // and a place button that never enabled — the salesperson's
              // main daily task, silently non-functional.
              //
              // Router state was the wrong home for it regardless: it is lost
              // on reload, and a phone held in a shop reloads constantly. The
              // cart is persisted to IndexedDB, so the order survives.
              cart.setCustomer(state.customerId, state.customerName ?? 'Customer');
              if (shopId) cart.setShop(shopId);
              navigate('/catalogue');
            }}
          >{t('visit.bookOrder')}</Button>
          <Button
            variant="ghost" block
            onClick={() => navigate(`/collect/${state.customerId ?? ''}`, {
              state: { shopId, shopName: state.shopName, outstandingPaise: state.outstandingPaise },
            })}
          >{t('visit.recordCollection')}</Button>
        </div>

        <hr className="divider" />

        <Card>
          <div className="stack-2">
            <label className="t-sm t-semi">{t('visit.outcome')}</label>
            <div className="row-wrap" style={{ gap: 8 }}>
              {OUTCOMES.map((o) => (
                <button
                  key={o}
                  className="chip"
                  style={{
                    padding: '9px 14px',
                    background: outcome === o ? 'var(--brand)' : 'var(--line-2)',
                    color: outcome === o ? 'var(--brand-ink)' : 'var(--ink-2)',
                    border: 'none', cursor: 'pointer', minHeight: 40,
                  }}
                  onClick={() => setOutcome(o)}
                  aria-pressed={outcome === o}
                >{t(`visit.${o}`)}</button>
              ))}
            </div>
          </div>
        </Card>

        {outcome === 'no_order' && (
          <Select
            label={t('visit.noOrderReason')}
            required
            value={noOrderReason}
            placeholder="Select"
            options={NO_ORDER_REASONS.map((r) => ({ value: r, label: t(`visit.${r}`) }))}
            onChange={(e) => setNoOrderReason(e.target.value)}
          />
        )}

        {outcome && (
          <>
            <TextArea
              label={t('visit.notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
            />
            {(outcome === 'follow_up_needed' || outcome === 'payment_promised') && (
              <TextInput
                label={t('visit.followUpDate')}
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
              />
            )}
            {outcome === 'competitor_push' && (
              <TextInput
                label={t('visit.competitor')}
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                maxLength={200}
              />
            )}
          </>
        )}

        {state.stopId && (
          <Button variant="quiet" block onClick={() => setSkipOpen(true)}>
            {t('beat.skip')}
          </Button>
        )}
      </div>

      {outcome && (
        <div className="actionbar">
          <Button block size="lg" busy={busy} onClick={() => void logVisit()}>
            {t('app.save')}
          </Button>
        </div>
      )}

      <Sheet
        open={skipOpen}
        onClose={() => setSkipOpen(false)}
        title={t('beat.skipReason')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setSkipOpen(false)}>{t('app.cancel')}</Button>
            <Button block busy={busy} disabled={!skipReason.trim()} onClick={() => void skip()}>
              {t('beat.skip')}
            </Button>
          </>
        }
      >
        <TextArea
          label={t('beat.skipReason')}
          value={skipReason}
          onChange={(e) => setSkipReason(e.target.value)}
          maxLength={300}
        />
      </Sheet>
    </Page>
  );
}
